import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axiosAuthInstance from '../../../utils/axios/axiosAuthInstance'
import { Field, Formik, FormikProvider, useFormik } from 'formik'
import CustomInput from '../../../components/common/CustomInput'
import CustomSelect from '../../../components/common/CustomSelect'
import CustomFile from '../../../components/common/CustomFile'
import * as Yup from 'yup'

// Validation
const validationSchema = Yup.object({
  category: Yup.string().trim().required('Business Category is Required'),
  type: Yup.string().trim().required('Company Type is Required'),
  name: Yup.string().trim().required('Company Name is Required'),
  year_of_est: Yup.number().required(' Year of Establishment is Required'),
  registration: Yup.string()
    .trim()
    .required('Company Registration is Required'),
  founder: Yup.string().trim().required('Founder Name is Required'),
  contact_person: Yup.string()
    .trim()
    .required('Contact Person Name is Required'),
  contact_person_role: Yup.string()
    .trim()
    .required('Contact Person Role is Required'),
  pan_number: Yup.string().trim().required('PAN Number is Required'),
  // pan_image: Yup.string().trim(). required("PAN Image is Required"),
  gst_number: Yup.string().trim().required('GST Number is Required'),
  // gst_image:Yup.string().trim().required("GST Image is Required"),
  account_number: Yup.string().trim().required('Account Number is Required'),
  ifsc: Yup.string().trim().required('IFSC is Required'),
})

const Add = () => {
  const [step, setStep] = useState(1)
  const [stepInfo, setStepInfo] = useState('')
  const [roleOptions, setRoleOptions] = useState([])
  const [typeOptions, setTypeOptions] = useState([])
  const [categoryOptions, setCategoryOptions] = useState([])
  const [isSubmitted, setIsSubmitted] = useState(false)
  
  const navigate = useNavigate()

  const fetchRoleData = async () => {
    try {
      const response = await axiosAuthInstance.get('role/dropdown')
      if (response && response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: item.name,
        }))
        setRoleOptions(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  const fetchTypeData = async () => {
    try {
      const response = await axiosAuthInstance.get('type/dropdown')
      if (response && response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: item.name,
        }))
        setTypeOptions(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  const fetchCategoryData = async () => {
    try {
      const response = await axiosAuthInstance.get('category/dropdown')
      if (response && response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: item.name,
        }))
        setCategoryOptions(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  useEffect(() => {
    fetchRoleData()
    fetchTypeData()
    fetchCategoryData()
  }, [])

  useEffect(() => {
    switch (step) {
      case 1:
        setStepInfo('Company Details')
        break

      case 2:
        setStepInfo('Company Bank Details')
        break

      default:
        setStepInfo('')
    }
  }, [step])

  const nextStep = () => {
    setStep((prev) => (prev < 2 ? prev + 1 : prev))
  }

  const prevStep = () => {
    setStep((prev) => (prev > 1 ? prev - 1 : prev))
  }

  const handleSubmit = async (values, { resetForm  }) => {
    setIsSubmitted(true)
    try {
      const formData = new FormData()
      formData.append('category', values.category)
      formData.append('type', values.type)
      formData.append('name', values.name)
      formData.append('year_of_est', values.year_of_est)
      formData.append('registration', values.registration)
      formData.append('founder', values.founder)
      formData.append('contact_person', values.contact_person)
      formData.append('contact_person_role', values.contact_person_role)
      formData.append('pan_number', values.pan_number)
      formData.append('pan_image', values.pan_image)
      formData.append('gst_number', values.gst_number)
      formData.append('gst_image', values.gst_image)
      formData.append('account_number', values.account_number)
      formData.append('ifsc', values.ifsc)


      const response = await axiosAuthInstance.post('company/add', formData)
      if (response.status === 200) {
        resetForm()
        navigate('/company')
      }
    } catch (error) {
      console.log(error)
    } finally {
      setIsSubmitted(false)
    }
  }
  const formik = useFormik({
    initialValues: {
      category: '',
      type: '',
      name: '',
      year_of_est: '',
      registration: '',
      founder: '',
      contact_person: '',
      contact_person_role: '',
      pan_number: '',
      pan_image: '',
      gst_number: '',
      gst_image: '',
      account_number: '',
      ifsc: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values, formikHelpers) => {
      const error = await formik.validateForm()
      if (Object.keys(error).length === 0) {
        handleSubmit(values, formikHelpers)
      } else {
        formikHelpers.setTouched(error)
      }
    },
  })

  const RegistrationOptions = [
    { value: '', label: 'LLP' },
    { value: 'PVT', label: 'PVT' },
    { value: 'Solo', label: 'Solo' },
  ]

  return (
    <FormikProvider value={formik}>
      <form className='flex flex-col p-4 gap-4' onSubmit={formik.handleSubmit}>
        <div>
          <label className='bg-gray-100 text-orange-800 rounded-full text-l font-medium me-2 px-2.5 py-0.5 dark:bg-orange-500 dark:text-white'>
            Step: {step}
          </label>
          {stepInfo && (
            <label className='bg-gray-100 text-orange-800 rounded-full text-l font-medium me-2 px-2.5 py-0.5 dark:bg-orange-500 dark:text-white'>
              {stepInfo}
            </label>
          )}
        </div>

        {step === 1 && (
          <>
            <Field
              name='category'
              label='Business Category'
              placeholder='Category'
              required
              component={CustomSelect}
              options={categoryOptions}
            />

            <Field
              name='type'
              label='Company Type'
              placeholder='Type'
              required
              component={CustomSelect}
              options={typeOptions}
            />

            <Field
              name='name'
              label='Company Name'
              placeholder='Company Name'
              required
              component={CustomInput}
            />

            <Field
              name='year_of_est'
              label=' Year Of Establishment'
              placeholder='Year Of Establishment'
              required
              component={CustomInput}
            />

            <Field
              name='registration'
              label='Registration'
              placeholder='Registration'
              required
              component={CustomSelect}
              options={RegistrationOptions}
            />

            <Field
              name='founder'
              label='Founder Name'
              placeholder='Founder Name'
              required
              component={CustomInput}
            />

            <Field
              name='contact_person'
              label='Contact Person Name'
              placeholder='Contact Person Name'
              required
              component={CustomInput}
            />

            <Field
              name='contact_person_role'
              label='Contact Person Role'
              placeholder='Contact Person Role'
              required
              component={CustomSelect}
              options={roleOptions}
            />
          </>
        )}

        {step === 2 && (
          <>
            <Field
              name='pan_number'
              label='PAN Number'
              placeholder='PAN Number'
              required
              component={CustomInput}
            />

            <Field
              name='pan_image'
              accept='image/*'
              label='PAN Image'
              placeholder='PAN Image'
              required
              component={CustomFile}
            />

            <Field
              name='gst_number'
              label='GST Number'
              placeholder='GST Number'
              required
              component={CustomInput}
            />

            <Field
              name='gst_image'
              accept='image/*'
              label='GST Image'
              placeholder='GST Image'
              required
              component={CustomFile}
            />

            <Field
              name='account_number'
              label='Account Number'
              placeholder='Account Number'
              required
              component={CustomInput}
            />

            <Field
              name='ifsc'
              label='IFSC'
              placeholder='IFSC'
              required
              component={CustomInput}
            />
          </>
        )}

        <div className='flex justify-end'>
          {step !== 1 && (
            <button
              type='button'
              onClick={prevStep}
              className='bg-gray-500 hover:bg-white text-xl font-medium mx-2 text-white hover:text-gray-500 border border-black rounded-lg px-6 py-2'
            >
              Previous
            </button>
          )}

          {step !== 2 && (
            <button
              type='button'
              onClick={nextStep}
              className='bg-primary hover:bg-white text-xl font-medium text-white hover:text-primary border border-black rounded-lg px-6 py-2'
            >
              Next
            </button>
          )}

          {step === 2 && (
            <button
            type="submit"
            disabled={isSubmitted}
            className="bg-primary hover:bg-white text-xl font-medium text-white hover:text-primary border border-black rounded-lg px-6 py-2"
          >
            {isSubmitted ? (
              <div className="w-5 h-5 mx-auto border-2 border-white border-solid rounded-full loader animate-spin border-t-transparent"></div>
               ) : (
              'Submit'
          )}
          </button>
          )}
        </div>
      </form>
    </FormikProvider>
  )
}

export default Add
